/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

/**
 * The CombinedSummaryValidator tests that the data that has been summarized in one widget is disjoint from data summarized in another.
 **/
class CombinedSummaryValidator {
	/**
	 * @Constructor
	 * @param {Object} widgetApi - the widgetApi for the widget being summarized.
	 */
	constructor(widgetApi, dashboardApi, logger) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.otherWidgetApi = null;
		this.logger = logger;
	}

	/**
	 * add an 'other' widgetApi to test against.
	 * The validate() method will determine if the summarized data in otherWidgetApi and summarized data in this widgetApi
	 * would lead to double-counting
	 * @param otherWidgetApi - the widget to compare against.
	 */
	combineSummariesWith(otherWidgetApi) {
		this.otherWidgetApi = otherWidgetApi;
	}

	/**
	 * The combined summary validator is only initialized once it has 2 widgetApi's to test.
	 */
	isInitialized() {
		return !!(this.widgetApi && this.otherWidgetApi);
	}

	/**
	 * Validate that the data in the widgets being combined are disjoint.
	 * Disjoint data can be combined (aka summed)
	 * Non-Disjoint data means summing would result in double-counting.
	 * @return true if the data being summarized in the two widgets can be combined.
	 */
	validate() {
		const thisVisAPI = this.widgetApi.getVisualization();
		const otherVisAPI = this.otherWidgetApi.getVisualization();

		//Same module? (if not, the answer is no)
		if ((thisVisAPI && otherVisAPI) && thisVisAPI.getDataSource().getId() === otherVisAPI.getDataSource().getId()) {
			return this._testForDisjointFilters(thisVisAPI, otherVisAPI);
		}

		//Disjoint local filters?
		return Promise.resolve(false);
	}

	//Use a server query to test if data in two widgets is disjoint or not.
	//Limitation: The two widgets must be against the same dataset.
	//
	//Perform the test by combining the filters of both widgets and executing a single item query for a measure.
	//If the data returned is null, the two visualizations are presenting disjoint data.
	_testForDisjointFilters(thisVisAPI, otherVisAPI) {
		const queryDef = this._createQuerySpecForDisjointFilterTest(thisVisAPI, otherVisAPI);
		if (!queryDef || !queryDef.dataItems || !queryDef.filters) {
			return Promise.resolve(false);
		}
		const content = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const visualization = content.getFeature('Visualization');
		const queryService = this.dashboardApi.getFeature('QueryService');
		const dataSource = visualization.getDataSource();

		const aggregations = queryDef.dataItems.map((dataItem) => dataItem.getAggregation());
		const columnIds = queryDef.dataItems.map((dataItem) => dataItem.getColumnId());
		const filters = queryDef.filters;

		return queryService.getColumnValues(dataSource.getId(), columnIds, filters, undefined, [], aggregations).then((queryResultData) => {
			//const queryResultData = queryService.createQueryResultData(testQueryResults);
			const rowCount = queryResultData && queryResultData.getRowCount();
			if (rowCount === 0) {
				return true;
			} else if (rowCount === 1) {
				const cellValue = queryResultData.getValue(0, 0);
				const projectedItemAggregate = queryDef.dataItems[0].getAggregation();
				if (projectedItemAggregate === 'count' || projectedItemAggregate === 'countdistinct') {
					//Countable aggregation types will come back as 0 when there is no intersection.
					return (cellValue && cellValue.v === 0) ? true : false;
				}
				return (cellValue && cellValue.v === null) ? true : false;
			}
			return false;
		}).catch(() => {
			//If the query fails for some reason, log the error but don't be destructive.  Simply return that the data is not disjoint.
			this.logger.error('An error occured while testing for disjoint filters', arguments, thisVisAPI);
			return false;
		});
	}

	/**
	 * Create a queryDefinition that can be used to detect whether filters are disjoint in two widgets
	 * If there are structures in the data that
	 * @returns a queryDef if the query should be performed or null if there is no need to query.
	 */
	_createQuerySpecForDisjointFilterTest(thisVisAPI, otherVisAPI) {
		let queryDef = {
			dataItems: [],
			filters: []
		};

		//Extract the first projected ordinal (really, could be any ordinal or any measure dataItem in the dataset)
		//Using a measure with no projected categories ensures a single row query result.
		const firstOrdinal = thisVisAPI.getSlots().getDataItemList().find((d)=> d.getType() === 'fact');
		const otherApiOrdinal = otherVisAPI.getSlots().getDataItemList().find((d)=> d.getType() === 'fact');

		if (!firstOrdinal || !otherApiOrdinal) {
			//No need to query...we can't support a chart that has no ordinals.
			return null;
		}

		queryDef.dataItems = [firstOrdinal];

		if (!thisVisAPI.getLocalFilters().getFilterList().length || !otherVisAPI.getLocalFilters().getFilterList().length) {
			//If either widget has no filters, there's no need to query....the data overlaps.
			return null;
		}

		thisVisAPI.getLocalFilters().getFilterList().forEach((filterEntry) => {
			queryDef.filters.push(filterEntry);
		});

		otherVisAPI.getLocalFilters().getFilterList().forEach((filterEntry) => {
			queryDef.filters.push(filterEntry);
		});

		return queryDef;
	}
}

export default CombinedSummaryValidator;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-summarizer/src/js/CombinedSummaryValidator.js